import React, { useState, useMemo } from 'react';
import {
  StorytellingFlight,
  StorytellingTransfer,
  StorytellingBus,
  StorytellingHotel,
  StorytellingTour,
  StorytellingCar,
  StorytellingInsurance,
  StorytellingPassport,
  StorytellingVisa,
  StorytellingResume
} from './screens';

/**
 * StorytellingFlow - Orquestrador principal
 * 
 * Responsabilidade: 
 * - Recebe a cotação completa
 * - Monta a sequência de telas dinamicamente baseado nos blocos
 * - Gerencia navegação entre telas
 * - Não sabe nada sobre preços (never shown to client)
 * 
 * Ordem da jornada:
 * 1. Brief fixo (Intro, Promise, DSC) - gerenciado externamente
 * 2. Voo de ida (primeiro voo)
 * 3. Transfer de ida (se houver)
 * 4. Hotéis (na ordem cronológica)
 * 5. Passeios (agrupados em uma tela)
 * 6. Carro (se houver)
 * 7. Seguro viagem (se houver)
 * 8. Transfer de volta (se houver)
 * 9. Voo de volta (último voo)
 * 10. Resumo final
 */

// Prioridade para ordenação dentro do mesmo dia
const TYPE_PRIORITY = {
  flight: 1,      // Voos primeiro
  bus: 2,         // Ônibus (similar a voo)
  transfer: 3,    // Depois transfers
  car: 4,         // Carro (pega no aeroporto)
  hotel: 5,       // Hotel (check-in)
  tour: 6,        // Passeios durante o dia
  insurance: 7,   // Seguro viagem
  passport: 8,    // Documentos
  visa: 9         // Documentos
};

// Narrativas padrão por tipo
const DEFAULT_NARRATIVES = {
  flight_outbound: 'A jornada começa aqui. Embarque tranquilo, chegue descansado e aproveite cada momento da sua viagem.',
  flight_return: 'De volta para casa, com o coração cheio de memórias. Sua jornada inesquecível chega ao fim, mas as lembranças permanecem para sempre.',
  transfer_outbound: 'Chegue ao seu destino com conforto e tranquilidade. Do aeroporto direto para o seu refúgio, sem preocupações.',
  transfer_return: 'Depois de dias incríveis, é hora de voltar. Uma despedida confortável para uma jornada inesquecível.',
  hotel: 'Não é só um lugar para dormir. É onde você vai descansar de verdade, acordar sem pressa e começar cada dia no seu ritmo.',
  tour: 'Experiências que vão ficar guardadas para sempre na memória. Momentos únicos aguardam por você.',
  car: 'Liberdade para explorar no seu ritmo. As melhores descobertas acontecem quando você escolhe o caminho.',
  bus_outbound: 'Embarque no conforto e curta a paisagem pelo caminho. A viagem já faz parte da experiência.',
  bus_return: 'De volta para casa, com a bagagem cheia de memórias. Relaxe e aproveite o trajeto.',
  insurance: 'Viaje com tranquilidade. Com o seguro viagem, você e sua família estão protegidos para aproveitar cada momento sem preocupações.',
  passport: 'O passaporte é o primeiro passo para explorar o mundo. A DSC Travel cuida de todo o processo para você — do formulário à entrega do documento em suas mãos.',
  visa: 'A DSC Travel cuida de toda a burocracia do visto americano para você. Documentação completa, formulário preenchido e acompanhamento do início ao fim.'
};

/**
 * Busca narrativa específica ou usa padrão
 * Prioridade: narrativa específica > narrativa genérica > padrão
 */
const getNarrative = (narratives, specificKey, genericKey) => {
  return narratives[specificKey] || narratives[genericKey] || DEFAULT_NARRATIVES[genericKey];
};

export default function StorytellingFlow({
  quote,
  narratives = {},  // Narrativas customizadas por destino
  onComplete,       // Chamado quando termina o flow
  onShare,
  whatsappNumber
}) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Monta a sequência de steps baseado nos blocos
  const steps = useMemo(() => {
    const blocks = quote.blocks || [];
    const result = [];

    // Separa blocos por tipo
    const flights = blocks.filter(b => b.type === 'flight');
    const transfers = blocks.filter(b => b.type === 'transfer');
    const buses = blocks.filter(b => b.type === 'bus');
    const hotels = blocks.filter(b => b.type === 'hotel');
    const tours = blocks.filter(b => b.type === 'tour');
    const cars = blocks.filter(b => b.type === 'car');
    const insurances = blocks.filter(b => b.type === 'insurance');
    const passports = blocks.filter(b => b.type === 'passport');
    const visas = blocks.filter(b => b.type === 'visa');

    // Ordena por data
    const sortByDate = (a, b) => new Date(a.date) - new Date(b.date);
    flights.sort(sortByDate);
    transfers.sort(sortByDate);
    buses.sort(sortByDate);
    hotels.sort(sortByDate);
    tours.sort(sortByDate);
    cars.sort(sortByDate);
    insurances.sort(sortByDate);

    // ═══ Agrupa voos por data (mesma data = mesma tela) ═══
    const flightsByDate = {};
    flights.forEach(flight => {
      const date = flight.date;
      if (!flightsByDate[date]) flightsByDate[date] = [];
      flightsByDate[date].push(flight);
    });

    // Para cada data, mescla segments e deduplica
    const flightGroups = Object.keys(flightsByDate)
      .sort()
      .map(date => {
        const dateFlights = flightsByDate[date];

        // Coleta todos os segments, deduplicando
        const allSegments = [];
        dateFlights.forEach(f => {
          (f.details?.segments || []).forEach(seg => {
            const isDuplicate = allSegments.some(existing =>
              existing.origin === seg.origin &&
              existing.destination === seg.destination &&
              existing.departure_time === seg.departure_time
            );
            if (!isDuplicate) allSegments.push(seg);
          });
        });

        // Ordena segments por horário de partida
        allSegments.sort((a, b) =>
          (`${a.departure_date || ''}T${a.departure_time || '00:00'}`).localeCompare(`${b.departure_date || ''}T${b.departure_time || '00:00'}`)
        );

        // Usa o primeiro block como base, sobrescreve segments com os mesclados
        const baseBlock = dateFlights[0];
        return {
          ...baseBlock,
          details: {
            ...baseBlock.details,
            segments: allSegments
          },
          price: dateFlights.reduce((sum, f) => sum + (parseFloat(f.price) || 0), 0)
        };
      });

    // Primeiro grupo = ida, último = volta, meio = trechos intermediários
    const outboundFlight = flightGroups[0] || null;
    const returnFlight = flightGroups.length > 1 ? flightGroups[flightGroups.length - 1] : null;
    const middleFlights = flightGroups.length > 2 ? flightGroups.slice(1, -1) : [];

    // Identifica transfer de ida e volta
    const outboundTransfer = transfers[0];
    const returnTransfer = transfers.length > 1 ? transfers[transfers.length - 1] : null;

    // Identifica ônibus de ida e volta
    const outboundBus = buses[0];
    const returnBus = buses.length > 1 ? buses[buses.length - 1] : null;

    // 1. Voo de ida (primeiro grupo de data)
    if (outboundFlight) {
      result.push({
        type: 'flight',
        component: 'StorytellingFlight',
        block: outboundFlight,
        isOutbound: true,
        narrative: getNarrative(narratives, 'flight_outbound', 'flight_outbound'),
        ctaText: middleFlights.length > 0 ? 'Ver próximo voo' : (outboundBus ? 'Ver trecho rodoviário' : (outboundTransfer ? 'Ver transfer de ida' : (hotels[0] ? 'Ver hospedagem' : 'Continuar')))
      });
    }

    // 1b. Voos intermediários (datas entre ida e volta)
    middleFlights.forEach((flight, idx) => {
      const isLastMiddle = idx === middleFlights.length - 1;
      let ctaText = 'Ver próximo voo';
      if (isLastMiddle) {
        ctaText = outboundBus ? 'Ver trecho rodoviário' : (outboundTransfer ? 'Ver transfer de ida' : (hotels[0] ? 'Ver hospedagem' : 'Continuar'));
      }
      result.push({
        type: 'flight',
        component: 'StorytellingFlight',
        block: flight,
        isOutbound: true,
        narrative: 'Próximo trecho da sua jornada. Cada voo te aproxima mais da experiência que espera por você.',
        ctaText
      });
    });

    // 1b. Ônibus de ida (após voo ou como primeiro transporte)
    if (outboundBus) {
      result.push({
        type: 'bus',
        component: 'StorytellingBus',
        block: outboundBus,
        isOutbound: true,
        narrative: getNarrative(narratives, 'bus_outbound', 'bus_outbound'),
        ctaText: outboundTransfer ? 'Ver transfer de ida' : (hotels[0] ? 'Ver hospedagem' : 'Continuar')
      });
    }

    // 2. Transfer de ida
    if (outboundTransfer) {
      result.push({
        type: 'transfer',
        component: 'StorytellingTransfer',
        block: outboundTransfer,
        isOutbound: true,
        narrative: getNarrative(narratives, 'transfer_outbound', 'transfer_outbound'),
        ctaText: hotels[0] ? 'Ver hospedagem' : 'Continuar'
      });
    }

    // 3. Carro (se pega no aeroporto, vem antes do hotel)
    if (cars.length > 0 && !outboundTransfer) {
      const carCity = cars[0].details?.city;
      result.push({
        type: 'car',
        component: 'StorytellingCar',
        block: cars[0],
        narrative: getNarrative(narratives, `car_${carCity}`, 'car'),
        ctaText: hotels[0] ? 'Ver hospedagem' : 'Continuar'
      });
    }

    // 4. Hotéis (na ordem cronológica)
    hotels.forEach((hotel, idx) => {
      const isLast = idx === hotels.length - 1;
      let ctaText = 'Continuar jornada';

      if (!isLast) {
        ctaText = 'Ver próxima hospedagem';
      } else if (tours.length > 0) {
        ctaText = 'Ver passeios incluídos';
      } else if (cars.length > 0 && outboundTransfer) {
        ctaText = 'Ver seu carro';
      } else if (insurances.length > 0) {
        ctaText = 'Ver seguro viagem';
      } else if (returnTransfer) {
        ctaText = 'Ver transfer de volta';
      } else if (returnFlight) {
        ctaText = 'Ver voo de volta';
      }

      const hotelName = hotel.title || hotel.details?.name;
      result.push({
        type: 'hotel',
        component: 'StorytellingHotel',
        block: hotel,
        narrative: getNarrative(narratives, `hotel_${hotelName}`, 'hotel'),
        ctaText
      });
    });

    // 5. Passeios (agrupados em uma única tela com carrossel)
    if (tours.length > 0) {
      let ctaText = 'Continuar jornada';
      if (cars.length > 0 && outboundTransfer) {
        ctaText = 'Ver seu carro';
      } else if (insurances.length > 0) {
        ctaText = 'Ver seguro viagem';
      } else if (returnTransfer) {
        ctaText = 'Ver transfer de volta';
      } else if (returnFlight) {
        ctaText = 'Ver voo de volta';
      }

      // Pega narrativa do primeiro tour ou usa padrão
      const firstTourName = tours[0].title || tours[0].details?.name;
      result.push({
        type: 'tour',
        component: 'StorytellingTour',
        blocks: tours, // Múltiplos tours
        narrative: getNarrative(narratives, `tour_${firstTourName}`, 'tour'),
        ctaText
      });
    }

    // 6. Carro (se tem transfer, carro vem depois dos passeios)
    if (cars.length > 0 && outboundTransfer) {
      const carCity = cars[0].details?.city;
      result.push({
        type: 'car',
        component: 'StorytellingCar',
        block: cars[0],
        narrative: getNarrative(narratives, `car_${carCity}`, 'car'),
        ctaText: insurances.length > 0 ? 'Ver seguro viagem' : (returnTransfer ? 'Ver transfer de volta' : (returnFlight ? 'Ver voo de volta' : 'Finalizar'))
      });
    }

    // 7. Seguro viagem
    if (insurances.length > 0) {
      const nextAfterInsurance = passports.length > 0 ? 'Ver passaporte' : (visas.length > 0 ? 'Ver visto americano' : (returnTransfer ? 'Ver transfer de volta' : (returnBus ? 'Ver trecho rodoviário de volta' : (returnFlight ? 'Ver voo de volta' : 'Ver resumo'))));
      result.push({
        type: 'insurance',
        component: 'StorytellingInsurance',
        block: insurances[0],
        narrative: getNarrative(narratives, 'insurance', 'insurance'),
        ctaText: nextAfterInsurance
      });
    }

    // 7b. Passaporte
    if (passports.length > 0) {
      const nextAfterPassport = visas.length > 0 ? 'Ver visto americano' : (returnTransfer ? 'Ver transfer de volta' : (returnBus ? 'Ver trecho rodoviário de volta' : (returnFlight ? 'Ver voo de volta' : 'Ver resumo')));
      result.push({
        type: 'passport',
        component: 'StorytellingPassport',
        block: passports[0],
        narrative: getNarrative(narratives, 'passport', 'passport'),
        ctaText: nextAfterPassport
      });
    }

    // 7c. Visto Americano
    if (visas.length > 0) {
      result.push({
        type: 'visa',
        component: 'StorytellingVisa',
        block: visas[0],
        narrative: getNarrative(narratives, 'visa', 'visa'),
        ctaText: returnTransfer ? 'Ver transfer de volta' : (returnBus ? 'Ver trecho rodoviário de volta' : (returnFlight ? 'Ver voo de volta' : 'Ver resumo'))
      });
    }

    // 8. Transfer de volta
    if (returnTransfer && returnTransfer !== outboundTransfer) {
      result.push({
        type: 'transfer',
        component: 'StorytellingTransfer',
        block: returnTransfer,
        isOutbound: false,
        narrative: getNarrative(narratives, 'transfer_return', 'transfer_return'),
        ctaText: returnBus ? 'Ver trecho rodoviário' : (returnFlight ? 'Ver voo de volta' : 'Finalizar cotação')
      });
    }

    // 9. Ônibus de volta
    if (returnBus && returnBus !== outboundBus) {
      result.push({
        type: 'bus',
        component: 'StorytellingBus',
        block: returnBus,
        isOutbound: false,
        narrative: getNarrative(narratives, 'bus_return', 'bus_return'),
        ctaText: returnFlight ? 'Ver voo de volta' : 'Finalizar cotação'
      });
    }

    // 10. Voo de volta
    if (returnFlight && returnFlight !== outboundFlight) {
      result.push({
        type: 'flight',
        component: 'StorytellingFlight',
        block: returnFlight,
        isOutbound: false,
        narrative: getNarrative(narratives, 'flight_return', 'flight_return'),
        ctaText: 'Finalizar cotação'
      });
    }

    // 10. Resumo final
    result.push({
      type: 'resume',
      component: 'StorytellingResume'
    });

    return result;
  }, [quote, narratives]);

  // Total de passos (incluindo os 3 do brief que são gerenciados externamente)
  const totalSteps = steps.length + 3; // +3 para Intro, Promise, DSC
  const currentStepNumber = currentStepIndex + 4; // Começa no 4 (após os 3 do brief)

  // Navegação
  const goToNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      onComplete?.();
    }
  };

  const goToPrev = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  // Renderiza o step atual
  const currentStep = steps[currentStepIndex];
  if (!currentStep) return null;

  const stepIndicator = null;

  switch (currentStep.component) {
    case 'StorytellingFlight':
      return (
        <StorytellingFlight
          block={currentStep.block}
          isOutbound={currentStep.isOutbound}
          stepIndicator={stepIndicator}
          narrativeText={currentStep.narrative}
          ctaText={currentStep.ctaText}
          onContinue={goToNext}
          onBack={goToPrev}
          onShare={onShare}
        />
      );

    case 'StorytellingTransfer':
      return (
        <StorytellingTransfer
          block={currentStep.block}
          isOutbound={currentStep.isOutbound}
          stepIndicator={stepIndicator}
          narrativeText={currentStep.narrative}
          ctaText={currentStep.ctaText}
          onContinue={goToNext}
          onBack={goToPrev}
          onShare={onShare}
        />
      );

    case 'StorytellingBus':
      return (
        <StorytellingBus
          block={currentStep.block}
          isOutbound={currentStep.isOutbound}
          stepIndicator={stepIndicator}
          narrativeText={currentStep.narrative}
          ctaText={currentStep.ctaText}
          onContinue={goToNext}
          onBack={goToPrev}
          onShare={onShare}
        />
      );

    case 'StorytellingHotel':
      return (
        <StorytellingHotel
          block={currentStep.block}
          stepIndicator={stepIndicator}
          narrativeText={currentStep.narrative}
          ctaText={currentStep.ctaText}
          onContinue={goToNext}
          onBack={goToPrev}
          onShare={onShare}
        />
      );

    case 'StorytellingTour':
      return (
        <StorytellingTour
          tours={currentStep.blocks}
          stepIndicator={stepIndicator}
          ctaText={currentStep.ctaText}
          onContinue={goToNext}
          onBack={goToPrev}
          onShare={onShare}
        />
      );

    case 'StorytellingCar':
      return (
        <StorytellingCar
          block={currentStep.block}
          stepIndicator={stepIndicator}
          narrativeText={currentStep.narrative}
          ctaText={currentStep.ctaText}
          onContinue={goToNext}
          onBack={goToPrev}
          onShare={onShare}
        />
      );

    case 'StorytellingInsurance':
      return (
        <StorytellingInsurance
          block={currentStep.block}
          stepIndicator={stepIndicator}
          narrativeText={currentStep.narrative}
          ctaText={currentStep.ctaText}
          onContinue={goToNext}
          onBack={goToPrev}
          onShare={onShare}
        />
      );

    case 'StorytellingPassport':
      return (
        <StorytellingPassport
          block={currentStep.block}
          stepIndicator={stepIndicator}
          narrativeText={currentStep.narrative}
          ctaText={currentStep.ctaText}
          onContinue={goToNext}
          onBack={goToPrev}
          onShare={onShare}
        />
      );

    case 'StorytellingVisa':
      return (
        <StorytellingVisa
          block={currentStep.block}
          stepIndicator={stepIndicator}
          narrativeText={currentStep.narrative}
          ctaText={currentStep.ctaText}
          onContinue={goToNext}
          onBack={goToPrev}
          onShare={onShare}
        />
      );

    case 'StorytellingResume':
      return (
        <StorytellingResume
          quote={quote}
          onBack={goToPrev}
          onShare={onShare}
          whatsappNumber={whatsappNumber}
        />
      );

    default:
      return null;
  }
}
